import type {ControlOption} from 'shells/card/segmented-control';
// @ts-expect-error - FxIcon is a valid icon
import {FxIcon} from 'shells/icon/svg/fx-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';

import type {
    ChatSettingsIntakeFormFieldsEnum,
    ChatSettingsIntakeFormTypeEnum,
    LinkNumberTypeEnum,
} from 'nutshell-graphql-types';

export type WidgetType = 'chat' | 'book' | 'email' | 'call' | 'text' | 'quote';

export const CHAT_WIDGET = 'chat';
export const BOOK_WIDGET = 'book';
export const EMAIL_WIDGET = 'email';
export const CALL_WIDGET = 'call';
export const TEXT_WIDGET = 'text';
export const QUOTE_WIDGET = 'quote';

export const MAX_LABEL_CHARACTER_LIMIT = 8;
export const MAX_EMAIL_ADDRESS_CHARACTER_LIMIT = 40;
export const SAVE_DELAY = 1000;

export const SAVE_STATE_KEYS = {
    DISCLAIMER: 'disclaimer',
    CHAT_LABEL: 'chat-label',
    CHAT_OPENING_MESSAGE: 'chat-opening-message',
    CHAT_SHOULD_SHOW_NUDGES: 'chat-should-show-nudges',
    CHAT_SHOULD_COLLECT_VISITOR_INFO: 'chat-should-collect-visitor-info',
    CHAT_INTAKE_FORM_TYPE: 'chat-intake-form-type',
    CHAT_INTAKE_FIELDS: 'chat-intake-fields',
    ALLOW_THREADS_WITHOUT_CONTACT: 'allow-threads-without-contact',
    CHAT_SHOULD_CREATE_LEADS: 'chat-should-create-leads',
    CHAT_LEADS_PIPELINE_ID: 'chat-leads-pipeline-id',
    BOOKING_LABEL: 'booking-label',
    BOOKING_MEETING_ID: 'booking-meeting-id',
    BOOKING_CONFIRMATION_MSG: 'booking-confirmation-message',
    BOOKING_SHOULD_CREATE_LEADS: 'booking-should-create-leads',
    BOOKING_LEADS_PIPELINE_ID: 'booking-pipeline-id',
    EMAIL_LABEL: 'email-label',
    EMAIL_FORM_SLUG: 'email-form-slug',
    EMAIL_CONFIRMATION_MSG: 'email-confirmation-message',
    EMAIL_SHOULD_CREATE_LEADS: 'email-should-create-leads',
    EMAIL_LEADS_PIPELINE_ID: 'email-pipeline-id',
    SHOULD_LINK_EMAIL: 'should-link-email',
    LINKED_EMAIL_ADDRESS: 'linked-email-address',
    CALL_LABEL: 'call-label',
    CALL_FORM_SLUG: 'call-form-slug',
    CALL_CONFIRMATION_MSG: 'call-confirmation-message',
    CALL_SHOULD_CREATE_LEADS: 'call-should-create-leads',
    CALL_LEADS_PIPELINE_ID: 'call-pipeline-id',
    CALL_SHOULD_LINK_PHONE: 'call-should-link-phone',
    CALL_LINKED_PHONE_NUMBER: 'call-linked-phone-number',
    TEXT_LABEL: 'text-label',
    TEXT_FORM_SLUG: 'text-form-slug',
    TEXT_CONFIRMATION_MSG: 'text-confirmation-message',
    TEXT_SHOULD_CREATE_LEADS: 'text-should-create-leads',
    TEXT_LEADS_PIPELINE_ID: 'text-pipeline-id',
    TEXT_SHOULD_LINK_PHONE: 'text-should-link-phone',
    TEXT_LINKED_PHONE_NUMBER: 'text-linked-phone-number',
    QUOTE_LABEL: 'quote-label',
    QUOTE_FORM_SLUG: 'quote-form-slug',
    QUOTE_CONFIRMATION_MSG: 'quote-confirmation-message',
    QUOTE_SHOULD_CREATE_LEADS: 'quote-should-create-leads',
    QUOTE_LEADS_PIPELINE_ID: 'quote-pipeline-id',
} as const;

export type SaveStateKey = (typeof SAVE_STATE_KEYS)[keyof typeof SAVE_STATE_KEYS];

export type IntakeFormSettings = {
    allowThreadsWithoutContact: boolean;
    type: ChatSettingsIntakeFormTypeEnum;
    fields: Array<ChatSettingsIntakeFormFieldsEnum>;
};

export const CHATBOT_COLLECT_VISITOR_INFORMATION_OPTIONS: ControlOption<ChatSettingsIntakeFormTypeEnum>[] =
    [
        {type: 'small', title: 'Via conversation', value: 'CONVERSATIONAL'},
        {type: 'small', title: 'Via form', value: 'REGULAR'},
    ];

export const NAME_FIELD = 'name';
export const EMAIL_FIELD = 'email';
export const PHONE_FIELD = 'phone';
export const COMPANY_FIELD = 'companyName';

export const formsNoOptionsMessage = {
    icon: <Illustration src='forms-magnifying-glass' width={180} />,
    text: 'No forms available. Once you create one, it will appear here.',
    cta: (
        <Link
            href='/marketing/forms/'
            to='/marketing/forms/'
            variant='button-primary'
            newTab={true}
        >
            Create a form
        </Link>
    ),
} as const;

export type ErrorState = {
    error: string | null | undefined;
    key: SaveStateKey;
};

export const getErrorMessage = (
    errorState: ErrorState | null | undefined,
    key: SaveStateKey
): string | null | undefined => {
    if (!errorState) {
        return null;
    }

    if (key !== errorState.key || !errorState.error) {
        return null;
    }

    return errorState.error;
};

export const CALL_TRACKER_OPTIONS: ControlOption<LinkNumberTypeEnum>[] = [
    {type: 'small', title: 'CallTrackerFX pool', value: 'callTracker', icon: FxIcon},
    {type: 'small', title: 'Custom phone number', value: 'custom'},
];
